import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Column,
  H1,
  InputCheckbox,
  ListDescription,
  LoadingEmptyState,
  MdPadding,
  MdSpacing,
  Row,
  SmSpacing,
  XlPadding,
  XxxxlSpacing,
} from '@alto/design-system';
import React from 'react';
import styled from 'styled-components';
import { type SubscriptionType, subscriptionsConstants } from '~shared/constants';
import { type Subscription } from '~shared/types/subscription';
import { PrefsConfirmationPage } from './PrefsConfirmationPage';
import logoAlto from '~web/images/altoLogo@2x.png';

type Props = {
  readonly email: string;
  readonly errorMessage?: string;
  readonly isUpdated: boolean;
  readonly isChanged: boolean;
  readonly subscriptions: Subscription[];
  readonly subscriptionsLoading: boolean;
  readonly handleUpdate: (unSubscribeAll?: boolean) => void;
  readonly setSubscriptions: (subscription: string) => void;
};

const LogoContainer = styled.a`
  width: 106px;
`;

const AltoLogo = styled.img`
  max-width: 100%;
  max-height: 100%;
`;

const StayInTouch = ({ email }: Pick<Props, 'email'>) => {
  return (
    <MdPadding>
      <Column gap="LG">
        <H1 center>We'd like to stay in touch!</H1>
        <Body
          center
          fontFamily="semibold"
        >
          Hi <strong>{email}</strong>, uncheck an option to remove yourself from a specific mailing list.
        </Body>
      </Column>
    </MdPadding>
  );
};

export const SubscriptionsPage = ({
  email,
  errorMessage,
  isUpdated,
  isChanged,
  subscriptions,
  setSubscriptions,
  subscriptionsLoading,
  handleUpdate,
}: Props) => {
  const renderContent = () =>
    subscriptionsLoading ? (
      <LoadingEmptyState />
    ) : (
      <>
        <StayInTouch email={email} />
        <XlPadding topPadding={SPACING.STATIC.NONE}>
          <Column gap="XS">
            {subscriptions.map(({ comms_type, subscribed }) => (
              <InputCheckbox
                noHighlight
                checkPlacement="left"
                name={comms_type}
                label={subscriptionsConstants[comms_type as SubscriptionType].category}
                descriptions={
                  <ListDescription>
                    {subscriptionsConstants[comms_type as SubscriptionType].description}
                  </ListDescription>
                }
                key={comms_type}
                value={subscribed}
                onPress={() => {
                  setSubscriptions(comms_type);
                }}
              />
            ))}
          </Column>
        </XlPadding>
        <Button
          disabled={!isChanged}
          label="Update"
          type="primary"
          onPress={handleUpdate}
        />
        <SmSpacing />
        <Button
          label="Or unsubscribe all"
          onPress={() => {
            handleUpdate(true);
          }}
          type="tertiary"
        />
        <MdSpacing />
        <Body
          center
          fontFamily="semibold"
          textSize="detail"
          color={COLORS.PALETTE.GREYSCALE.DARKER}
        >
          <strong>Please note:</strong> you may still receive account-related emails from Alto related to the management
          and delivery of your medications.
        </Body>
      </>
    );

  return (
    <Column
      flexShrink={0}
      flexGrow={1}
      centerHorizontally
      backgroundColor={COLORS.BACKGROUND_COLORS.WHITE}
    >
      <Row center>
        <MdPadding>
          <LogoContainer
            href="https://alto.com"
            aria-label="Alto Logo"
          >
            <AltoLogo src={logoAlto} />
          </LogoContainer>
        </MdPadding>
      </Row>
      <XxxxlSpacing />
      {errorMessage ? <Body center>{errorMessage}</Body> : null}
      {isUpdated ? <PrefsConfirmationPage /> : renderContent()}
    </Column>
  );
};
